import { useEffect, useState } from "react"

/**
 * Shared editorial primitives for the QuiteRight design system.
 *
 * Used by the boutique-secret-weapon LP, the homepage, and any future pages
 * that adopt the "auction catalogue × cinema" aesthetic. Pulled out of the LP
 * so both pages reference the same source of truth.
 *
 * Aesthetic frame: "Savile Row tailor who happens to use lasers" — generous
 * Playfair serif, tracked-cap labels, hairline rules, ornamental diamond
 * dividers, restrained gold accents, full-bleed charcoal cinema stages.
 */

/* ------------------------------------------------------------------ */
/* Section eyebrow — tracked-cap label flanked by hairline rules       */
/* ------------------------------------------------------------------ */

interface SectionEyebrowProps {
  label: string
  tone?: "red" | "gold" | "stone"
}

export function SectionEyebrow({ label, tone = "red" }: SectionEyebrowProps) {
  const color =
    tone === "gold" ? "text-gold" : tone === "stone" ? "text-warm-grey" : "text-red"
  return (
    <div className={`flex items-center justify-center gap-4 ${color}`}>
      <span className="h-px w-12 bg-current opacity-40" />
      <span className="text-[10px] font-semibold uppercase tracking-[0.32em]">
        {label}
      </span>
      <span className="h-px w-12 bg-current opacity-40" />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Ornament rule — centred diamond between hairlines                   */
/* ------------------------------------------------------------------ */

interface OrnamentRuleProps {
  tone?: "stone" | "gold" | "red"
}

export function OrnamentRule({ tone = "stone" }: OrnamentRuleProps) {
  const color =
    tone === "gold" ? "text-gold" : tone === "red" ? "text-red" : "text-stone"
  return (
    <div className={`flex items-center justify-center gap-3 ${color}`}>
      <span className="h-px w-20 bg-current opacity-50" />
      <span className="text-[10px]">◆</span>
      <span className="h-px w-20 bg-current opacity-50" />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Scroll progress bar — thin red line at the top of the viewport      */
/* ------------------------------------------------------------------ */

export function ScrollProgress() {
  const [progress, setProgress] = useState(0)
  // Transitions are intentionally disabled for the very first paint so the
  // browser can't animate from its default (no transform = full bar) to our
  // initial scaleX(0). Without this gate the bar visibly snaps full → empty
  // every page load.
  const [transitionsEnabled, setTransitionsEnabled] = useState(false)

  useEffect(() => {
    let raf = 0
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight
      const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0
      setProgress(p)
      raf = 0
    }
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(update)
    }
    update()
    // Enable smooth transitions one frame after the initial scaleX(0) has
    // been painted, so subsequent scroll updates animate but mount doesn't.
    const enableRaf = requestAnimationFrame(() => setTransitionsEnabled(true))
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      cancelAnimationFrame(enableRaf)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-50 h-[3px] bg-charcoal/10"
    >
      <div
        className={`h-full origin-left bg-red ${
          transitionsEnabled ? "transition-transform duration-150 ease-out" : ""
        }`}
        style={{ transform: `scaleX(${progress})` }}
      />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Editorial style block — paste-once CSS for reveal/stagger/pulse     */
/* ------------------------------------------------------------------ */

/**
 * Drop this once at the top of any page that uses [data-reveal],
 * [data-reveal-stagger], or [data-cta-pulse] attributes. The CSS is
 * inlined here so the page is fully self-contained.
 */
export function EditorialStyles() {
  return (
    <style>{`
      [data-reveal] {
        opacity: 0;
        transform: translateY(28px);
        transition: opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1), transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
      }
      [data-reveal].is-visible {
        opacity: 1;
        transform: translateY(0);
      }

      [data-reveal-stagger] {
        opacity: 0;
        transform: translateY(20px);
        animation: lp-rise 1s cubic-bezier(0.22, 1, 0.36, 1) forwards;
      }
      [data-reveal-stagger="1"] { animation-delay: 0.05s; }
      [data-reveal-stagger="2"] { animation-delay: 0.20s; }
      [data-reveal-stagger="3"] { animation-delay: 0.35s; }
      [data-reveal-stagger="4"] { animation-delay: 0.55s; }
      [data-reveal-stagger="5"] { animation-delay: 0.75s; }
      [data-reveal-stagger="6"] { animation-delay: 0.95s; }
      [data-reveal-stagger="7"] { animation-delay: 1.10s; }

      @keyframes lp-rise {
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      [data-cta-pulse] {
        animation: lp-cta-pulse 4.5s ease-in-out infinite;
      }
      @keyframes lp-cta-pulse {
        0%, 100% { box-shadow: 0 18px 40px -18px rgba(196,30,42,0.65); }
        50% { box-shadow: 0 22px 55px -15px rgba(196,30,42,0.85); }
      }

      @media (prefers-reduced-motion: reduce) {
        [data-reveal], [data-reveal-stagger] {
          opacity: 1 !important;
          transform: none !important;
          animation: none !important;
          transition: none !important;
        }
        [data-cta-pulse] {
          animation: none !important;
        }
      }
    `}</style>
  )
}

/* ------------------------------------------------------------------ */
/* SVG film grain overlay — atmospheric noise for cinematic stages     */
/* ------------------------------------------------------------------ */

/**
 * Drop this inside a relatively-positioned container with overflow hidden
 * to add a subtle film-grain texture (4% opacity, mix-blend-overlay).
 * Used on charcoal cinematic stages.
 */
export function FilmGrain() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
      }}
    />
  )
}
